import { AxiosError } from 'axios';
import DatePicker from 'components/base/DatePicker';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import { DiscountDetailsForm } from './CreateDiscountForm';
import useBranchList from 'pages/apps/hooks/useGetBranchList';

type UpdateDiscountFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

interface UpdateDiscountForm {
  discountName: string;
  description: string;
  discountAmount: number;
  discountPercentage: number;
  minimumProductAmount: number;
  minimumInvoiceAmount: number;
  maximumDiscountAmount: number;
  usageLimit: number;
  startDate: string | Date;
  endDate: string | Date;
  branchId: number | null;
  isAfterSalesDiscount: boolean;
  isForAllCustomers: boolean;
  numberOfDaysBeforeActive: number;
  isForMembers: boolean;
}

interface UpdateDiscountFormProps extends UpdateDiscountForm {
  discountId: number;
}

const UpdateDiscountForm = forwardRef<
  UpdateDiscountFormHandle,
  UpdateDiscountFormProps
>(
  (
    {
      discountId,
      discountName,
      description,
      discountAmount,
      discountPercentage,
      minimumProductAmount,
      minimumInvoiceAmount,
      maximumDiscountAmount,
      usageLimit,
      startDate,
      endDate,
      branchId,
      isAfterSalesDiscount,
      isForAllCustomers,
      numberOfDaysBeforeActive,
      isForMembers
    },
    ref
  ) => {
    const [discountDetails, setDiscountDetails] = useState<UpdateDiscountForm>({
      discountName: discountName,
      description: description,
      discountAmount: discountAmount,
      discountPercentage: discountPercentage,
      minimumProductAmount: minimumProductAmount,
      minimumInvoiceAmount: minimumInvoiceAmount,
      maximumDiscountAmount: maximumDiscountAmount,
      usageLimit: usageLimit,
      startDate: startDate,
      endDate: endDate,
      branchId: branchId,
      isAfterSalesDiscount: isAfterSalesDiscount,
      isForAllCustomers: isForAllCustomers,
      numberOfDaysBeforeActive: numberOfDaysBeforeActive,
      isForMembers: isForMembers
    });
    const branchList = useBranchList();
    console.log(startDate);
    const handleFormChange = <K extends keyof DiscountDetailsForm>(
      field: K,
      value: DiscountDetailsForm[K]
    ) => {
      setDiscountDetails(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const UpdateDiscountFormSchema = yup.object().shape({
      discountName: yup.string().required('Discount name is required'),
      description: yup.string().required('Description is required'),
      minimumProductAmount: yup
        .number()
        .min(1, 'Minimum product amount must be greater than 0')
        .required(),
      branchId: yup.number().required('Branch is required')
    });

    const handleSubmit = async () => {
      const formattedDiscountDetails = {
        ...discountDetails,
        startDate: formatToUTCISO(discountDetails.startDate),
        endDate: formatToUTCISO(discountDetails.endDate)
      };
      try {
        await UpdateDiscountFormSchema.validate(formattedDiscountDetails, {
          abortEarly: false
        });
        await axiosInstance.put(
          `/api/v1/discounts/${discountId}`,
          formattedDiscountDetails
        );
        toast.success('Discount Created', { ...toastConfig, autoClose: 2000 });
        return true;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          error.inner.forEach(err => {
            toast.error(err.message, { ...toastConfig, autoClose: 3000 });
          });
        } else if (error instanceof AxiosError)
          toast.error(error.response?.data.errorMessage, {
            ...toastConfig,
            autoClose: 3000
          });
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit
    }));

    return (
      <Form className="mb-3">
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Discount Name</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.discountName}
              onChange={e => handleFormChange('discountName', e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Discount Amount</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.discountAmount ?? ''}
              onChange={e =>
                handleFormChange('discountAmount', +e.target.value)
              }
              disabled={discountDetails.discountPercentage !== null}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          {' '}
          <Form.Group as={Col}>
            <Form.Label>description</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              value={discountDetails.description}
              onChange={e => handleFormChange('description', e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Discount Perentage</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.discountPercentage ?? ''}
              onChange={e =>
                handleFormChange('discountPercentage', +e.target.value)
              }
              disabled={discountDetails.discountAmount !== null}
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Minimum Product Amount</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.minimumProductAmount}
              onChange={e =>
                handleFormChange('minimumProductAmount', +e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Minimum Invoice Amount</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.minimumInvoiceAmount}
              onChange={e =>
                handleFormChange('minimumInvoiceAmount', +e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Maximum Invoice Amount</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.maximumDiscountAmount}
              onChange={e =>
                handleFormChange('maximumDiscountAmount', +e.target.value)
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Usage Limit</Form.Label>
            <Form.Control
              type="text"
              value={discountDetails.usageLimit}
              onChange={e => handleFormChange('usageLimit', +e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              value={
                discountDetails.startDate instanceof Date
                  ? discountDetails.startDate
                  : new Date(discountDetails.startDate)
              }
              onChange={date =>
                handleFormChange('startDate', (date as Date[])[0])
              }
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>End Date</Form.Label>
            <DatePicker
              value={
                discountDetails.endDate instanceof Date
                  ? discountDetails.endDate
                  : new Date(discountDetails.endDate)
              }
              onChange={date =>
                handleFormChange('endDate', (date as Date[])[0])
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Branch</Form.Label>
            <Form.Control
              as="select"
              value={discountDetails.branchId as number} // needs to be changed if the backendis ready
              onChange={e => handleFormChange('branchId', +e.target.value)}
            >
              <option value="">Select Branch</option>
              {branchList.map(branch => (
                <option value={branch.branchId} key={branch.branchId}>
                  {branch.branchName}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Is After Sales Discount</Form.Label>
            <Form.Control
              as="select"
              value={discountDetails.isAfterSalesDiscount.toString()}
              onChange={e =>
                handleFormChange(
                  'isAfterSalesDiscount',
                  e.target.value === 'true'
                )
              }
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Form.Control>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Is For All Customers</Form.Label>
            <Form.Control
              as="select"
              value={discountDetails.isForAllCustomers.toString()}
              onChange={e =>
                handleFormChange('isForAllCustomers', e.target.value === 'true')
              }
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Form.Control>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Number of Days Before Active</Form.Label>
            <Form.Control
              type="number"
              value={discountDetails.numberOfDaysBeforeActive}
              onChange={e =>
                handleFormChange('numberOfDaysBeforeActive', +e.target.value)
              }
            />
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Is For Members</Form.Label>
            <Form.Control
              as="select"
              value={discountDetails.isForMembers.toString()}
              onChange={e =>
                handleFormChange('isForMembers', e.target.value === 'true')
              }
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Form.Control>
          </Form.Group>
        </Row>
      </Form>
    );
  }
);

export default UpdateDiscountForm;
